import type { Route } from '../ui/nav.svelte';
import type { SyncEvent } from './state';

type WatchRoute = Extract<Route, { name: 'watch' }>;

/** The keyed Watch instance owns a title, not the mutable global route. */
export function watchSession(readRoute: () => Route) {
  const initial = readRoute();
  let alive = true;
  // Includes a claim inherited from the preceding keyed title while this one loads.
  let heldRoom: string | null = initial.name === 'watch' ? initial.roomId : null;
  const current = (): WatchRoute | null => {
    const route = readRoute();
    return alive && initial.name === 'watch' && route.name === 'watch' &&
      route.kind === initial.kind && route.imdbId === initial.imdbId ? route : null;
  };
  return {
    current,
    hold(roomId: string) { heldRoom = roomId; },
    /** A keyed title replacement in the SAME room transfers the screen claim. */
    destroy(withdraw: () => void) {
      alive = false;
      const next = readRoute();
      if (!heldRoom || next.name !== 'watch' || next.roomId !== heldRoom) withdraw();
    },
    /** Defer navigation out of the reducer, then recheck both screen and room. */
    listen(
      roomId: string,
      subscribe: (fn: (event: SyncEvent) => void) => () => void,
      ended: (route: WatchRoute) => void
    ) {
      let stopped = false;
      const off = subscribe(event => {
        if (event.type !== 'room-ended' || event.roomId !== roomId) return;
        queueMicrotask(() => {
          const route = current();
          if (!stopped && route?.roomId === roomId) ended(route);
        });
      });
      return () => { stopped = true; off(); };
    }
  };
}
